/**
 * Resource Service
 *
 * Handles resource management API calls:
 * - List resources
 * - Get resource details
 * - Create/update/delete resources
 * - Resource actions (start, stop, restart, etc.)
 * - Resource metrics
 * - Snapshots
 * - Firewall rules
 */

import { api, ApiPagination } from "@/lib/api";
import type { CustomAxiosRequestConfig } from "@/lib/api";
import {
  Resource,
  ResourceListParams,
  ResourceListResponse,
  ResourceNameAvailability,
  ReinstallQuota,
  ResourceMetrics,
  BackendResourceMetricsResponse,
  MetricsTimeRange,
  Snapshot,
  FirewallRule,
  FirewallOptions,
  FirewallLog,
  FirewallMacro,
  PricingCalculation,
  ResourceSpecs,
  type ResourceStatus,
  ResourceCredentials,
  type CredentialAccessKind,
  ResetVmPasswordResult,
  VMStatusSummaryDto,
  CreateFirewallRuleRequest,
  ResourceConsole,
  ObjectStorageBucketsResponse,
  ObjectStorageCredentials,
  CreateBucketRequest,
  CreateBucketResponse,
  UpdateBucketQuotaResponse,
  ResetStoragePasswordResponse,
  ProvisioningStatus,
  ResourceProvisioningInfo,
  type ProvisioningRetryCheck,
  type DeleteResourcePayload,
  type DeletedResourceDto,
  type ResourceStatusDto,
} from "@/types/resource";
import {
  type RenewalOrder,
  type RenewalPreview,
  type ResourceOrderSummary,
} from "@/types/resource-subscription";
import type { OSImage, ProductLineDto } from "@/types/osImage";
import type { CurrentDiskInfo } from "@/types/upgrade";
import {
  transformToPricingConfig,
  parsePricingBreakdown,
  PRICING_COMPONENT_SLUGS,
} from "@/constants/specificationKeys";
import { fromMinorUnits, type CurrencyCodeMetadata } from "@/lib/currency";

/** A MoneyDto as the resource endpoints send it. */
interface BackendMoney {
  amount: number; // Smallest currency unit
  decimalAmount: number; // Decimal representation
  currency: string;
  formattedAmount: string;
}

/**
 * Carries the discounted monthly cost across to the frontend Resource.
 *
 * Every screen reads `effectiveMonthlyCost ?? monthlyCost`, so a mapper that drops this field
 * silently shows the undiscounted list rate — the VM table read 39,000 for a 12-month machine the
 * summary card above it correctly priced at 33,150. Undefined when the backend sent none, which
 * leaves that fallback to the list rate in place.
 */
const toEffectiveMonthlyCost = (money?: BackendMoney | null) =>
  money ? { amount: money.decimalAmount, currency: money.currency } : undefined;

/**
 * Backend Resource structure (as returned by API)
 */
interface BackendResource {
  id: string;
  /** Provider-side VM id. Null while the record is linked to no machine. Mirrors ResourceListDto. */
  providerResourceId?: string | null;
  orderId?: string | null;
  tenantId?: string;
  name: string;
  type: string;
  status: string;
  osImageName: string;
  cores: number;
  memory: number;
  disk?: number;
  bandwidth?: number;
  publicIpAddress: string | null;
  region: string;
  monthlyCost: {
    amount: number; // Smallest currency unit (renamed from amountCents)
    decimalAmount: number; // Decimal representation
    currency: string;
    formattedAmount: string;
  };
  /** What the customer pays per month after the commitment discount. Mirrors ResourceListDto. */
  effectiveMonthlyCost?: BackendMoney | null;
  hourlyCost?: {
    amount: number; // Smallest currency unit
    decimalAmount: number; // Decimal representation
    currency: string;
    formattedAmount: string;
  };
  createdAt: string;
  updatedAt?: string;
  provisionedAt?: string;
  expiresAt?: string | null;
  cpuUsagePercent?: number;
  memUsagePercent?: number;
  lastAction?: {
    type: string;
    timestamp: string;
  };
  provisioning?: ResourceProvisioningInfo;
  /** A row reading Decommissioning is being restored after a deletion, not reinstalled. */
  isRestoring?: boolean;
}

export interface UpdateResourceRequest {
  name?: string;
  specs?: ResourceSpecs;
}

export interface DashboardSummary {
  totalResources: number;
  totalResourcesTrend: number;
  activeVMs: number;
  activeVMsTrend: number;
  currentBalance: number;
  pendingInvoices: number;
  resourceStatusDistribution: {
    active: number;
    stopped: number;
    failed: number;
    provisioning: number;
  };
  monthToDateSpending: number;
  projectedMonthlyCost: number;
  recentActivity: RecentActivity[];

  // Extended report fields (optional - may not be returned by backend initially)
  usageTrends?: UsageTrendsData;
  costBreakdown?: CostBreakdownData;
  topResources?: TopResourceData[];
  quotaUtilization?: QuotaUtilizationData;
  performanceMetrics?: PerformanceMetricsData;
  resourceGrowth?: ResourceGrowthData;
}

export interface RecentActivity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  icon?: string;
}

// Supporting interfaces for extended dashboard data
export interface UsageTrendsData {
  cpuAverage: number;
  memoryAverage: number;
  diskAverage: number;
  last7Days: Array<{
    timestamp: string;
    cores: number;
    memory: number;
    disk: number;
  }>;
}

export interface CostBreakdownData {
  total: number;
  byResourceType: Array<{
    type: string;
    cost: number;
    count: number;
    percentage: number;
  }>;
}

export interface TopResourceData {
  id: string;
  name: string;
  type: string;
  monthlyCost: number;
  hourlyCost: number;
  status: string;
  region: string;
}

export interface QuotaUtilizationData {
  resources: QuotaData;
  cpuCores: QuotaData;
  memoryGB: QuotaData;
  storageGB: QuotaData;
}

export interface QuotaData {
  used: number;
  limit: number;
  percentage: number;
}

export interface PerformanceMetricsData {
  avgUptimePercentage: number;
  avgResponseTimeMs: number;
  errorRate: number;
  totalUptimeHours: number;
}

export interface ResourceGrowthData {
  last30Days: Array<{
    date: string;
    created: number;
    terminated: number;
    net: number;
  }>;
  growthRate: number;
}

export interface CreateSnapshotRequest {
  name: string;
  description?: string;
}

/**
 * Builds the /pricing/calculate URL with the optional region and currency query parameters.
 */
function buildPricingUrl(region?: string, currency?: string): string {
  const params = new URLSearchParams();
  if (region) params.set("region", region);
  if (currency) params.set("currency", currency);
  const queryString = params.toString();
  return queryString
    ? `/pricing/calculate?${queryString}`
    : "/pricing/calculate";
}

/**
 * Adapts the backend pricing response (smallest currency units) to PricingCalculation.
 *
 * Shared by every pricing call so a quote cannot be interpreted differently per resource type —
 * the rate, the commitment discounts and the VAT rate are all read from the response, never
 * reconstructed on the client.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function adaptPricingResponse(data: any): PricingCalculation {
  // Get currency from response, default to USD
  const currency = (data?.currency || "USD") as CurrencyCodeMetadata;

  // Convert from smallest currency unit to display amount using currency-aware conversion
  const convertFromMinorUnits = (amount?: number) =>
    typeof amount === "number" ? fromMinorUnits(amount, currency) : 0;

  // Derive totalMonthly first (directly from monthlyPrice if available).
  // For monthly-billed SKUs, unitPrice/totalPrice is the monthly rate, not hourly —
  // so compute hourly as monthlyPrice / 730 to avoid showing 730× the correct value.
  const totalMonthly = data?.monthlyPrice
    ? convertFromMinorUnits(data.monthlyPrice)
    : convertFromMinorUnits(data?.unitPrice ?? data?.totalPrice) * 730;
  const totalHourly = totalMonthly / 730;

  // Parse component breakdown using centralized function with currency
  const rawBreakdown: Record<string, number> = data?.componentBreakdown || {};
  const parsedBreakdown = parsePricingBreakdown(rawBreakdown, currency);

  // Convert raw breakdown values from minor units to display amounts
  const convertedBreakdown: Record<string, number> = {};
  for (const [key, value] of Object.entries(rawBreakdown)) {
    convertedBreakdown[key] = convertFromMinorUnits(value);
  }

  // Convert billingCycleOptions from minor units to display units (same conversion as
  // totalMonthly/totalHourly above). Backend BuildBillingCycleOptionsAsync builds these
  // from MonthlyPrice which is in smallest currency unit.
  const billingCycleOptions = data?.billingCycleOptions?.map(
    (opt: {
      months: number;
      discountPercent: number;
      totalPrice: number;
      monthlyEquivalent: number;
    }) => ({
      ...opt,
      totalPrice: convertFromMinorUnits(opt.totalPrice),
      monthlyEquivalent: convertFromMinorUnits(opt.monthlyEquivalent),
    }),
  );

  return {
    cpuCost: parsedBreakdown.cores,
    memoryCost: parsedBreakdown.memory,
    diskCost: parsedBreakdown.disk,
    totalMonthly,
    totalHourly,
    currency: data?.currency || "USD",
    vatRate: data?.vatRate,
    billingCycleOptions,
    componentBreakdown:
      Object.keys(convertedBreakdown).length > 0
        ? convertedBreakdown
        : undefined,
  };
}

/**
 * Resource Service Methods
 */
export const resourceService = {
  getProvisioningStatus: async (
    resourceId: string,
  ): Promise<ProvisioningStatus> => {
    const response = await api.get<ProvisioningStatus>(
      `/resources/${resourceId}/provisioning-status`,
    );
    return response;
  },
  /**
   * List resources with pagination and filters
   */
  list: async (params?: ResourceListParams): Promise<ResourceListResponse> => {
    const response = await api.getList<BackendResource>("/resources", {
      params,
    });

    return {
      resources: response.data.map((r) => ({
        id: r.id,
        providerResourceId: r.providerResourceId ?? undefined,
        orderId: r.orderId ?? undefined,
        tenantId: r.tenantId || "",
        name: r.name,
        type: r.type,
        status: r.status as ResourceStatus,
        osImage: {
          id: "",
          name: r.osImageName || "",
          version: "",
          iconUrl: "",
        },
        specs: {
          cores: r.cores || 0,
          memory: r.memory || 0,
          disk: r.disk || 0,
          bandwidth: r.bandwidth,
        },
        ipAddress: r.publicIpAddress || undefined,
        region: r.region,
        hourlyCost: r.hourlyCost
          ? {
              amount: r.hourlyCost.decimalAmount,
              currency: r.hourlyCost.currency,
            }
          : { amount: 0, currency: r.monthlyCost?.currency || "VND" },
        monthlyCost: {
          amount: r.monthlyCost?.decimalAmount || 0,
          currency: r.monthlyCost?.currency || "VND",
        },
        effectiveMonthlyCost: toEffectiveMonthlyCost(r.effectiveMonthlyCost),
        createdAt: r.createdAt,
        updatedAt: r.updatedAt || r.createdAt,
        provisionedAt: r.provisionedAt,
        expiresAt: r.expiresAt ?? undefined,
        cpuUsagePercent: r.cpuUsagePercent,
        memUsagePercent: r.memUsagePercent,
        lastAction: r.lastAction?.type,
        provisioning: r.provisioning,
        isRestoring: r.isRestoring ?? false,
      })),
      total: response.pagination.totalItems,
      page: response.pagination.currentPage,
      limit: response.pagination.itemsPerPage,
      totalPages: response.pagination.totalPages,
    };
  },

  /**
   * Check whether a resource name is available within the current tenant.
   * `GET /resources/check-name`
   */
  checkNameAvailability: async (
    name: string,
  ): Promise<ResourceNameAvailability> => {
    return api.get<ResourceNameAvailability>("/resources/check-name", {
      params: { name },
      _skipServerRetry: true,
      _suppressErrorToast: true,
    });
  },

  /**
   * Get resource by ID
   */
  get: async (
    resourceId: string,
    options?: { suppressErrorToast?: boolean },
  ): Promise<Resource> => {
    const response = await api.get<{
      id: string;
      providerResourceId?: string | null;
      orderId?: string | null;
      tenantId?: string;
      name: string;
      description?: string;
      type: string;
      status: string;
      osImage?: {
        id: string;
        name: string;
        category: string;
        iconUrl: string | null;
      };
      specifications: {
        cores: number;
        memory: number;
        disk: number;
        templateId?: string;
        templateName?: string;
        storageTier?: string;
        bandwidthTier?: string;
      };
      configuration?: Record<string, unknown>;
      publicIpAddress: string | null;
      region: string;
      availabilityZone?: string | null;
      hourlyCost?: {
        amount: number; // Smallest currency unit
        decimalAmount: number; // Decimal representation
        currency: string;
        formattedAmount: string;
      };
      monthlyCost: {
        amount: number; // Smallest currency unit
        decimalAmount: number; // Decimal representation
        currency: string;
        formattedAmount: string;
      };
      effectiveMonthlyCost?: BackendMoney | null;
      provisionedAt: string | null;
      suspendedAt?: string | null;
      expiresAt?: string | null;
      createdAt: string;
      updatedAt: string;
      tags?: string[];
      lastAction?: {
        type: string;
        timestamp: string;
      };
      provisioning?: ResourceProvisioningInfo;
      reinstallStartedAt?: string | null;
      isRestoring?: boolean;
      deletionFailedAt?: string | null;
    }>(`/resources/${resourceId}`, {
      _suppressErrorToast: options?.suppressErrorToast,
    } as CustomAxiosRequestConfig);

    // Transform backend response to frontend Resource type
    return {
      id: response.id,
      providerResourceId: response.providerResourceId ?? undefined,
      orderId: response.orderId ?? undefined,
      tenantId: response.tenantId || "",
      name: response.name,
      type: response.type,
      status: response.status as ResourceStatus,
      osImage: response.osImage
        ? {
            id: response.osImage.id,
            name: response.osImage.name,
            // The detail endpoint reports no version — the template name already carries
            // it. `category` is the OS family ("Windows"/"Linux") and must never be shown
            // as a version, or the label reads "Windows-10-Pro linux".
            version: "",
            iconUrl: response.osImage.iconUrl || "",
          }
        : {
            id: "",
            name: "",
            version: "",
            iconUrl: "",
          },
      specs: {
        // Map specifications → specs
        cores: response.specifications.cores,
        memory: response.specifications.memory,
        disk: response.specifications.disk,
        bandwidth: undefined,
        storageTier: response.specifications.storageTier,
        bandwidthTier: response.specifications.bandwidthTier,
        maxSnapshots: response.configuration?.maxSnapshots as
          | number
          | undefined,
        templateName: response.specifications.templateName,
      },
      ipAddress: response.publicIpAddress || undefined, // Map publicIpAddress → ipAddress
      region: response.region,
      hourlyCost: response.hourlyCost
        ? {
            amount: response.hourlyCost.decimalAmount, // Use decimalAmount for dollar value
            currency: response.hourlyCost.currency,
          }
        : { amount: 0, currency: "USD" },
      monthlyCost: {
        amount: response.monthlyCost.decimalAmount, // Use decimalAmount for dollar value
        currency: response.monthlyCost.currency,
      },
      effectiveMonthlyCost: toEffectiveMonthlyCost(
        response.effectiveMonthlyCost,
      ),
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      provisionedAt: response.provisionedAt || undefined,
      expiresAt: response.expiresAt ?? undefined,
      lastAction: response.lastAction ? response.lastAction.type : undefined,
      // Mapped like the list endpoints do. Dropping it here left the detail page unable to
      // explain a failed provisioning at all: its tracker renders the failure branch only when
      // this object is present, and the refund vs manual-verification wording is chosen from
      // `failureCode` inside it.
      provisioning: response.provisioning,
      // The rebuild popup's clock; without it the popup shows no elapsed time at all.
      reinstallStartedAt: response.reinstallStartedAt ?? null,
      isRestoring: response.isRestoring ?? false,
      deletionFailedAt: response.deletionFailedAt ?? null,
    };
  },

  /**
   * Create standalone disk resource
   */
  createDisk: async (data: {
    name: string;
    description?: string;
    sizeGB: number;
    storageTier: string;
  }): Promise<Resource> => {
    // Map frontend form data to backend CreateDiskRequest contract
    const request = {
      name: data.name,
      description: data.description || "",
      sizeGB: data.sizeGB,
      storageTier: data.storageTier.toUpperCase(), // SSD, HDD, or NVME
    };

    return api.post("/resources/disks", request);
  },

  /**
   * Create disk and automatically attach it to a VM in one operation
   */
  createAndAttachDisk: async (
    vmId: string,
    data: {
      name: string;
      description?: string;
      sizeGB: number;
      storageTier: string;
      deviceId?: string;
      busType?: "scsi" | "sata" | "virtio";
      isReadOnly?: boolean;
    },
  ): Promise<{
    diskResource: Resource;
    attachmentId: string;
  }> => {
    const request = {
      name: data.name,
      description: data.description || "",
      sizeGB: data.sizeGB,
      storageTier: data.storageTier.toUpperCase(),
      deviceId: data.deviceId,
      busType: data.busType || "scsi",
      isReadOnly: data.isReadOnly || false,
    };

    const response = await api.post<{
      diskResource: Resource;
      attachmentId: string;
    }>(`/resources/${vmId}/disks`, request);

    return response;
  },

  /**
   * Create standalone network resource
   */
  createNetwork: async (data: {
    name: string;
    bandwidthTier: string;
    vlanId?: number;
  }): Promise<Resource> => {
    // Map frontend form data to backend CreateNetworkRequest contract
    const request = {
      name: data.name,
      bandwidthTier: data.bandwidthTier,
      vlanId: data.vlanId,
    };

    return api.post("/resources/networks", request);
  },

  /**
   * Update resource
   */
  update: (
    resourceId: string,
    data: UpdateResourceRequest,
  ): Promise<Resource> => {
    return api.patch(`/resources/${resourceId}`, data);
  },

  /**
   * Update resource specifications (CPU, RAM, Disk)
   */
  updateSpecs: async (
    resourceId: string,
    specs: { cores: number; memory: number; disk: number },
  ): Promise<Resource> => {
    return api.put(`/resources/${resourceId}/specs`, specs);
  },

  /**
   * Delete resource. A VM requires the confirmation payload (reason + the name typed back);
   * disks, networks and buckets send an empty body, since the backend requires one.
   */
  delete: (
    resourceId: string,
    payload?: DeleteResourcePayload,
  ): Promise<void> => {
    // `_skipServerRetry` on this and every power verb below: none of them is safe to replay. The
    // handler awaits the provider before answering, so a gateway 5xx can arrive after the change
    // was made — the interceptor's automatic retry would then reboot a VM again, or come back
    // refused and report a failure for an action that succeeded.
    // A VM (the one with a payload) answers RESOURCE_RUNNING inside DeleteVMDialog, with the shutdown
    // step or the status-lag message, so the interceptor's toast would say the same thing twice.
    return api.delete(`/resources/${resourceId}`, {
      data: payload ?? {},
      _skipServerRetry: true,
      ...(payload ? { _suppressErrorToastFor: ["RESOURCE_RUNNING"] } : {}),
    });
  },

  /**
   * The caller's VMs deleted in the last hour, newest first — the "Pending deletion" tab. Each can
   * be restored (`restore`) until that window passes, then it is deleted for good.
   * `GET /resources/deleted`
   */
  getDeleted: (): Promise<DeletedResourceDto[]> => {
    return api.get("/resources/deleted");
  },

  /**
   * Current lifecycle state of up to 100 of the caller's resources, keyed by id. Ids the caller
   * does not own, or that do not exist, are simply absent; deleted resources are included.
   *
   * `ids` goes out repeated (`ids=a&ids=b`) — the shape ASP.NET binds to an array. Axios's own
   * serializer would write `ids[]=a`, which binds to nothing. It is a background poll, so a
   * failure stays silent rather than toasting every interval.
   */
  getStatuses: (ids: string[]): Promise<Record<string, ResourceStatusDto>> => {
    const params = new URLSearchParams();
    for (const id of ids) {
      params.append("ids", id);
    }

    return api.get("/resources/statuses", {
      params,
      _suppressErrorToast: true,
      // The watch polls again on its own interval, so a 5xx here does not need the interceptor's
      // own retry-with-backoff on top of it — that combination is what turned one failed tick into
      // a burst of requests.
      _skipServerRetry: true,
    } as CustomAxiosRequestConfig);
  },

  /**
   * Restores one of the caller's deleted VMs, inside its window, on its uuid and current OS. The VM is
   * back in the list reading Decommissioning and turns Running when the rebuild is done.
   * `POST /resources/{id}/restore`
   */
  restore: (resourceId: string): Promise<void> => {
    // Not replayed after a 5xx: the restore is queued at the provider, so a retry could queue it twice.
    // The page reports a refusal itself (VMsPage restoreMutation), so the interceptor must not toast it too.
    return api.post(`/resources/${resourceId}/restore`, undefined, {
      _skipServerRetry: true,
      _suppressErrorToast: true,
    });
  },

  /**
   * How many reinstalls the VM has left under the reinstall limit.
   * `GET /resources/{id}/reinstall/quota`. A courtesy read for the reinstall dialog: a failure is
   * not toasted, because the reinstall request itself is the gate.
   */
  getReinstallQuota: async (resourceId: string): Promise<ReinstallQuota> => {
    return api.get<ReinstallQuota>(`/resources/${resourceId}/reinstall/quota`, {
      _skipServerRetry: true,
      _suppressErrorToast: true,
    });
  },

  /**
   * The in-place reinstall of one of the caller's live, Stopped VMs on its uuid. `confirmationName`
   * and `reason` are required by the backend; omit `templateId` to keep the VM's current OS.
   */
  reinstall: (
    resourceId: string,
    payload: {
      templateId?: string;
      confirmationName: string;
      reason: string;
    },
  ): Promise<void> => {
    // Not replayed after a 5xx: the reinstall is queued at the provider, so a retry could queue
    // it twice.
    return api.post(`/resources/${resourceId}/reinstall`, payload, {
      _skipServerRetry: true,
    });
  },

  /**
   * Whether "Retry provisioning" may be offered for each of the given resources right now. Batched
   * (max 100 ids) so a list screen with several failed VMs on one page makes one call. Send only
   * ids of resources whose status is Error — every other status is refused the same way the
   * mutation itself would refuse it, so there is nothing worth checking. Every requested id comes
   * back with an entry.
   */
  checkProvisioningRetry: (
    resourceIds: string[],
  ): Promise<Record<string, ProvisioningRetryCheck>> => {
    return api.post("/resources/retry-provisioning/check", { resourceIds });
  },

  /**
   * Retries provisioning a VM stuck in Error, at no extra charge. Offered only after
   * `checkProvisioningRetry` answers `allowed: true` (status Error, a provisioning failure, order
   * and billing cycle checked out, created within the last 24 hours) — the server re-checks
   * everything and refuses with one of RESOURCE_NOT_A_VM, RESOURCE_STATUS_NOT_RETRYABLE,
   * RESOURCE_ALREADY_PROVISIONED, NOT_A_PROVISIONING_FAILURE, ORDER_REQUIRED,
   * ORDER_NOT_ELIGIBLE, ORDER_NOT_PAID, ORDER_REFUNDED, BILLING_CYCLE_NOT_USABLE,
   * PROVISIONING_IN_PROGRESS, PROVISIONING_RETRY_CONFLICT, PROVISIONING_OUTCOME_UNKNOWN or
   * RETRY_WINDOW_EXPIRED.
   */
  retryProvisioning: (
    resourceId: string,
  ): Promise<{ resumesExistingVm: boolean }> => {
    // Not replayed after a 5xx: a create call may have reached the provider even without a
    // clear answer, so a retried retry could queue a second create.
    return api.post(`/resources/${resourceId}/retry-provisioning`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Start resource
   */
  start: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/start`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Stop resource
   */
  stop: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/stop`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Stop the machine at the provider and start it again — what applies an upgraded vCPU and memory,
   * which a reboot does not. A stopped machine is only started. The caller shows the refusal itself.
   */
  stopStart: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/stop-start`, undefined, {
      _skipServerRetry: true,
      _suppressErrorToast: true,
    });
  },

  /**
   * Restart resource
   */
  restart: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/restart`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Suspend resource
   */
  suspend: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/suspend`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Resume suspended resource
   */
  resume: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/resume`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Open a renewal order for a resource's current configuration. The order is then
   * settled through the ordinary payment flow (QR, bank transfer or account balance),
   * and the subscription is extended once that payment lands — nothing is charged here.
   * A renewal order already awaiting payment comes back instead of a new one, flagged
   * `isExistingOrder`, so the customer finishes what they started rather than being refused.
   */
  createRenewalOrder: (
    resourceId: string,
    months: number,
  ): Promise<RenewalOrder> => {
    // Not replayed after a 5xx: the order may already exist, and a retry would open a second one.
    return api.post(
      `/resources/${resourceId}/renew/order`,
      { months },
      { _skipServerRetry: true },
    );
  },

  /**
   * Preview a renewal: the stack being kept and every commitment length on offer, each
   * with its full breakdown (regular, discount, subtotal, VAT, total) and the paid-through
   * date it would produce. Priced by the same service the order is built from.
   * The client `api` wrapper already prepends /api/v1, so pass only the suffix.
   */
  getRenewalPreview: (resourceId: string): Promise<RenewalPreview> => {
    return api.get(`/resources/${resourceId}/renew/preview`);
  },

  /**
   * Every order that touches this resource — the purchase that created it, any upgrade, and every
   * renewal — newest first. A renewal is its own order, so this is the only place the machine's
   * full spend appears together.
   */
  getOrders: (resourceId: string): Promise<ResourceOrderSummary[]> => {
    return api.get(`/resources/${resourceId}/orders`);
  },

  /**
   * Shutdown resource (graceful stop via OS)
   */
  shutdown: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/shutdown`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Reset resource (force restart)
   */
  reset: (resourceId: string): Promise<void> => {
    return api.post(`/resources/${resourceId}/reset`, undefined, {
      _skipServerRetry: true,
    });
  },

  /**
   * Get resource metrics with time-series data
   * Transforms backend response to frontend format with unit conversions
   */
  getMetrics: async (
    resourceId: string,
    timeRange: MetricsTimeRange = "1h",
    options?: {
      /** No toast and no axios retry: the caller falls back quietly (the VM list's usage bars). */
      quiet?: boolean;
    },
  ): Promise<ResourceMetrics> => {
    const response = await api.get<BackendResourceMetricsResponse>(
      `/resources/${resourceId}/metrics`,
      {
        params: { timeRange },
        _suppressErrorToast: options?.quiet,
        _skipServerRetry: options?.quiet,
      },
    );

    // Transform backend response to frontend format
    // Backend provides:
    // - CPU: percentage (0-100)
    // - Memory: percentage (0-100) and MB
    // - Network: Mbps (megabits per second)
    // - Disk I/O: bytes/sec

    // Convert bytes/sec to MB/s for display (divide by 1024*1024)
    const bytesToMB = (value: number) => value / (1024 * 1024);

    return {
      // CPU already in percentage (0-100)
      cores: (response.cpuUsage ?? []).map((point) => ({
        timestamp: point.timestamp,
        value: point.value,
      })),

      // Memory already in percentage (0-100)
      memory: (response.memoryUsagePercent ?? []).map((point) => ({
        timestamp: point.timestamp,
        value: point.value,
      })),

      // Network: Already in Mbps from backend (megabits per second)
      network: {
        inbound: (response.networkInMbps ?? []).map((point) => ({
          timestamp: point.timestamp,
          value: point.value,
        })),
        outbound: (response.networkOutMbps ?? []).map((point) => ({
          timestamp: point.timestamp,
          value: point.value,
        })),
      },

      // Disk I/O: Convert bytes/sec to MB/s for display
      disk: {
        read: (response.diskReadBytesPerSec ?? []).map((point) => ({
          timestamp: point.timestamp,
          value: bytesToMB(point.value),
        })),
        write: (response.diskWriteBytesPerSec ?? []).map((point) => ({
          timestamp: point.timestamp,
          value: bytesToMB(point.value),
        })),
      },
    };
  },

  /**
   * Get disk layout for a VM resource from WiService
   * Returns current disk devices with size and boot flag
   */
  getResourceDisks: async (resourceId: string): Promise<CurrentDiskInfo[]> => {
    // Both callers (the upgrade page and its applied-specs card) render their own error.
    return api.get<CurrentDiskInfo[]>(`/resources/${resourceId}/disks`, {
      _suppressErrorToast: true,
    });
  },

  /**
   * Get dashboard summary
   */
  getDashboardSummary: (): Promise<DashboardSummary> => {
    return api.get("/dashboard/summary");
  },

  /**
   * Get available OS images
   */
  getOSImages: (): Promise<OSImage[]> => {
    return api.get("/catalog/os-images");
  },

  /**
   * Get available product lines from the catalog
   */
  getProductLines: (): Promise<ProductLineDto[]> => {
    return api.get("/catalog/product-lines");
  },

  /**
   * Calculate pricing for specs
   */
  calculatePricing: (
    specs: ResourceSpecs,
    region?: string,
    productLineSlug?: string,
    currency?: string,
  ): Promise<PricingCalculation> => {
    // Map frontend specs to backend pricing request contract using centralized transform
    const baseConfig = transformToPricingConfig(specs);
    const requestBody = {
      productSlug: "general-purpose-vm",
      configuration: productLineSlug
        ? { ...baseConfig, productline: productLineSlug }
        : baseConfig,
      quantity: 1,
    };

    return api
      .post(buildPricingUrl(region, currency), requestBody, {
        _skipServerRetry: true,
        _suppressErrorToast: true,
      })
      .then(adaptPricingResponse);
  },

  /**
   * Calculate pricing for an Object Storage quota.
   *
   * Quotes the same way the VM wizard does — per-GB rate, commitment discounts and VAT all come
   * from the backend, which is also what the order will charge. The storage wizard used to
   * compute all three client-side from constants, so an admin changing a rate never reached the
   * customer and the advertised price could not be trusted (see WICL-430 for the VM equivalent).
   *
   * The configuration key must be the `object-storage-gb` PricingComponent slug: that is how the
   * pricing engine resolves the quantity, and how the order path sends it after
   * SpecificationKeys.ToPricingConfiguration maps `quotaGb`.
   */
  calculateObjectStoragePricing: (
    quotaGb: number,
    region?: string,
    currency?: string,
  ): Promise<PricingCalculation> => {
    const requestBody = {
      productSlug: "object-storage",
      configuration: {
        [PRICING_COMPONENT_SLUGS.OBJECT_STORAGE_GB]: quotaGb,
      },
      quantity: 1,
    };

    return api
      .post(buildPricingUrl(region, currency), requestBody)
      .then(adaptPricingResponse);
  },

  /**
   * Get snapshots for a resource
   */
  getSnapshots: (resourceId: string): Promise<Snapshot[]> => {
    return api.get(`/resources/${resourceId}/snapshots`);
  },

  /**
   * Create snapshot
   * Returns executionId for tracking the async operation
   */
  createSnapshot: (
    resourceId: string,
    data: CreateSnapshotRequest,
  ): Promise<{ executionId: number }> => {
    // Snapshot writes start a provider job before answering, so like the power verbs they must
    // not be replayed after a 5xx: a retry would queue a second snapshot or a second rollback.
    return api.post(`/resources/${resourceId}/snapshots`, data, {
      _skipServerRetry: true,
    });
  },

  /**
   * Delete snapshot by name
   * Returns executionId for tracking the async operation
   */
  deleteSnapshot: async (
    resourceId: string,
    snapshotName: string,
  ): Promise<{ executionId: number }> => {
    return api.delete(
      `/resources/${resourceId}/snapshots/${encodeURIComponent(snapshotName)}`,
      { _skipServerRetry: true },
    );
  },

  /**
   * Rollback VM to snapshot (VM must be stopped)
   * Returns executionId for tracking the async operation
   */
  rollbackSnapshot: async (
    resourceId: string,
    snapshotName: string,
  ): Promise<{ executionId: number }> => {
    return api.post(
      `/resources/${resourceId}/snapshots/${encodeURIComponent(snapshotName)}/rollback`,
      undefined,
      { _skipServerRetry: true },
    );
  },

  /**
   * Update snapshot description
   */
  updateSnapshotDescription: async (
    resourceId: string,
    snapshotName: string,
    description: string,
  ): Promise<void> => {
    return api.patch(
      `/resources/${resourceId}/snapshots/${encodeURIComponent(snapshotName)}/description`,
      { description },
    );
  },

  /**
   * Get firewall rules and options
   */
  getFirewallRules: async (
    resourceId: string,
  ): Promise<{ rules: FirewallRule[]; options: FirewallOptions }> => {
    return api.get(`/resources/${resourceId}/firewall/rules`);
  },

  /**
   * Add firewall rule
   */
  addFirewallRule: (
    resourceId: string,
    data: CreateFirewallRuleRequest,
  ): Promise<void> => {
    // Firewall writes are not replayed after a 5xx either. Rules are addressed by position, so a
    // replayed edit or delete lands on whatever rule has since moved into that slot, and a
    // replayed add leaves a duplicate — the same hazard the tab's saving overlay exists to stop.
    return api.post(`/resources/${resourceId}/firewall/rules`, data, {
      _skipServerRetry: true,
    });
  },

  /**
   * Update firewall rule
   */
  updateFirewallRule: (
    resourceId: string,
    pos: number,
    data: CreateFirewallRuleRequest,
  ): Promise<void> => {
    return api.patch(`/resources/${resourceId}/firewall/rules/${pos}`, data, {
      _skipServerRetry: true,
    });
  },

  /**
   * Delete firewall rule
   */
  deleteFirewallRule: (resourceId: string, pos: number): Promise<void> => {
    return api.delete(`/resources/${resourceId}/firewall/rules/${pos}`, {
      _skipServerRetry: true,
    });
  },

  /**
   * Update firewall options
   */
  updateFirewallOptions: (
    resourceId: string,
    options: FirewallOptions,
  ): Promise<void> => {
    return api.put(`/resources/${resourceId}/firewall/options`, options, {
      _skipServerRetry: true,
    });
  },

  /**
   * Get firewall logs
   */
  getFirewallLogs: (resourceId: string): Promise<FirewallLog[]> => {
    return api.get(`/resources/${resourceId}/firewall/logs`);
  },

  /**
   * Get firewall macros
   */
  getFirewallMacros: (): Promise<FirewallMacro[]> => {
    return api.get("/resources/firewall/macros");
  },

  // POST /resources/bulk/start and /resources/bulk/stop are deliberately not wrapped here. Both
  // endpoints still exist on the backend, but their handlers only write Status to the database
  // and never call WiService, so the VMs stayed as they were while the UI reported success — until
  // the 5-minute status sync silently put the old status back. Bulk restart, suspend and resume
  // fan out to the single-resource endpoints instead, which do reach the provider.
  //
  // There is no bulk delete either, and the endpoint behind it is gone: nothing on this console
  // ever called it, and it skipped both guards the single delete depends on — the typed reason and
  // the VM name typed back — which are the only thing standing between a stray click and a wiped
  // machine. Deleting is one VM at a time, through DeleteVMDialog.

  /**
   * Get resources with filtering (for attachment manager)
   * @param params - Filter parameters (type, status, pagination)
   * @returns Paginated list of resources
   */
  getResources: async (params?: {
    type?: "VirtualMachine" | "Storage" | "Network";
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: Resource[];
    pagination: ApiPagination;
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append("type", params.type);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize)
      queryParams.append("limit", params.pageSize.toString());

    const response = await api.getList<BackendResource>(
      `/resources${queryParams.toString() ? `?${queryParams}` : ""}`,
    );

    // Transform backend resources to frontend Resource type
    const transformedResources: Resource[] = response.data.map((r) => ({
      id: r.id,
      providerResourceId: r.providerResourceId ?? undefined,
      orderId: r.orderId ?? undefined,
      tenantId: r.tenantId || "",
      name: r.name,
      type: r.type,
      status: r.status as ResourceStatus,
      osImage: {
        id: "",
        name: r.osImageName || "",
        version: "",
        iconUrl: "",
      },
      specs: {
        cores: r.cores || 0,
        memory: r.memory || 0,
        disk: r.disk || 0,
        bandwidth: r.bandwidth,
      },
      ipAddress: r.publicIpAddress || undefined,
      region: r.region,
      hourlyCost: r.hourlyCost
        ? {
            amount: r.hourlyCost.decimalAmount, // Use decimalAmount for dollar value
            currency: r.hourlyCost.currency,
          }
        : { amount: 0, currency: "USD" },
      monthlyCost: {
        amount: r.monthlyCost?.decimalAmount || 0, // Use decimalAmount for dollar value
        currency: r.monthlyCost?.currency || "USD",
      },
      effectiveMonthlyCost: toEffectiveMonthlyCost(r.effectiveMonthlyCost),
      createdAt: r.createdAt,
      updatedAt: r.updatedAt || r.createdAt,
      provisionedAt: r.provisionedAt,
      cpuUsagePercent: r.cpuUsagePercent,
      memUsagePercent: r.memUsagePercent,
      lastAction: r.lastAction?.type,
      provisioning: r.provisioning,
      isRestoring: r.isRestoring ?? false,
    }));

    return {
      data: transformedResources,
      pagination: response.pagination,
    };
  },

  /**
   * Get VM status summary statistics
   */
  getVMStatusSummary: async (): Promise<VMStatusSummaryDto> => {
    return api.get("/resources/vms/summary");
  },

  /**
   * Get resource credentials (username/password for VM access)
   */
  getCredentials: async (resourceId: string): Promise<ResourceCredentials> => {
    return api.get(`/resources/${resourceId}/credentials`);
  },

  /**
   * Record in the audit trail that the customer revealed or copied the VM password. The server
   * cannot see either happen, so the console reports it.
   */
  recordCredentialAccess: async (
    resourceId: string,
    kind: CredentialAccessKind,
  ): Promise<void> => {
    return api.post(
      `/resources/${resourceId}/credentials/access-events`,
      { kind },
      {
        // Bookkeeping the customer did not ask for: a failure must not interrupt them with a toast,
        // and a blind retry would write the same row twice.
        _suppressErrorToast: true,
        _skipServerRetry: true,
      },
    );
  },

  /**
   * Set a new OS password on a VM. The new password comes back once, in this response.
   *
   * Pass `password` to use the customer's own choice; omit it and the infrastructure generates one,
   * which is what this call always did. The body is left off entirely in that case — the endpoint
   * accepts a request with no body at all.
   *
   * Route is `/vm/reset-password`, not `/reset-password`: object storage already owns the latter on
   * the same controller.
   */
  resetVmPassword: async (
    resourceId: string,
    password?: string,
  ): Promise<ResetVmPasswordResult> => {
    return api.post(
      `/resources/${resourceId}/vm/reset-password`,
      // Truthy, not `!== undefined`: absent, null and empty all mean "let the infrastructure
      // generate one" — the provider's own reading of an empty `password`, which WiCloud passes
      // through rather than giving empty a third meaning. Omitting the body is the tidiest way to
      // say it.
      password ? { password } : undefined,
      {
        // The caller renders the failure in place, and keeps it on screen. The interceptor's own
        // toast would fire alongside it and, on a 5xx, replace the message with a generic
        // "unexpected server error" — which on PASSWORD_APPLIED_NOT_STORED throws away the only
        // recovery there is.
        _suppressErrorToast: true,
        // Never retried blind either. With no password supplied, every attempt generates a NEW one
        // and applies it to the machine, so the automatic 5xx backoff would leave the VM holding a
        // password that is not the one the last response reported. A chosen password is idempotent
        // and would survive a retry, but the flag stays on for both: one rule for one endpoint is
        // easier to keep true than a flag that depends on what the caller passed.
        _skipServerRetry: true,
      },
    );
  },

  /**
   * Get console URL for VM access
   */
  getConsole: async (resourceId: string): Promise<ResourceConsole> => {
    return api.get(`/resources/${resourceId}/console`, {
      _suppressErrorToast: true,
    });
  },

  /**
   * Get attachments for a VM (disks and networks attached to this VM)
   */
  getAttachments: async (
    vmId: string,
  ): Promise<{
    disks: Array<{
      attachmentId: string;
      resourceId: string;
      resourceName: string;
      sizeGB: number;
      deviceId: string;
      iops: number;
      attachedAt: string;
    }>;
    networks: Array<{
      attachmentId: string;
      resourceId: string;
      resourceName: string;
      bandwidth: string;
      deviceId: string;
      vlanId?: number;
      attachedAt: string;
    }>;
  }> => {
    const response = await api.get<{
      disks: Array<{
        attachmentId: string;
        resourceId: string;
        resourceName: string;
        sizeGB: number;
        deviceId: string;
        iops: number;
        attachedAt: string;
      }>;
      networks: Array<{
        attachmentId: string;
        resourceId: string;
        resourceName: string;
        bandwidth: string;
        deviceId: string;
        vlanId?: number;
        attachedAt: string;
      }>;
    }>(`/resources/${vmId}/attachments`);

    return response;
  },

  /**
   * Get parent VMs that a disk or network resource is attached to
   */
  getParentAttachments: async (
    resourceId: string,
  ): Promise<{
    parentVMs: Array<{
      attachmentId: string;
      parentResourceId: string;
      parentResourceName: string;
      deviceId: string;
      attachedAt: string;
      busType?: string;
      bootOrder?: number;
      isReadOnly?: boolean;
      vlanId?: number;
    }>;
  }> => {
    const response = await api.get<{
      parentVMs: Array<{
        attachmentId: string;
        parentResourceId: string;
        parentResourceName: string;
        deviceId: string;
        attachedAt: string;
        busType?: string;
        bootOrder?: number;
        isReadOnly?: boolean;
        vlanId?: number;
      }>;
    }>(`/resources/${resourceId}/parent-attachments`);

    return response;
  },

  /**
   * Attach disk to VM
   */
  attachDisk: async (
    vmId: string,
    diskId: string,
    options?: {
      deviceId?: string;
      busType?: "scsi" | "sata" | "virtio";
      bootOrder?: number;
      isReadOnly?: boolean;
    },
  ): Promise<{ attachmentId: string }> => {
    return api.post(`/resources/${vmId}/disks/${diskId}/attach`, options || {});
  },

  /**
   * Detach disk from VM
   */
  detachDisk: async (vmId: string, diskId: string): Promise<void> => {
    return api.post(`/resources/${vmId}/disks/${diskId}/detach`);
  },

  /**
   * Attach network to VM
   */
  attachNetwork: async (
    vmId: string,
    networkId: string,
    options?: {
      deviceId?: string;
      vlanId?: number;
    },
  ): Promise<{ attachmentId: string }> => {
    return api.post(
      `/resources/${vmId}/networks/${networkId}/attach`,
      options || {},
    );
  },

  /**
   * Detach network from VM
   */
  detachNetwork: async (vmId: string, networkId: string): Promise<void> => {
    return api.post(`/resources/${vmId}/networks/${networkId}/detach`);
  },

  // ==========================================
  // Object Storage Methods
  // ==========================================

  getObjectStorageBuckets: async (
    resourceId: string,
  ): Promise<ObjectStorageBucketsResponse> => {
    return api.get(`/resources/${resourceId}/buckets`);
  },

  getObjectStorageCredentials: async (
    resourceId: string,
  ): Promise<ObjectStorageCredentials> => {
    return api.get(`/resources/${resourceId}/storage-credentials`);
  },

  createObjectStorageBucket: async (
    resourceId: string,
    request: CreateBucketRequest,
  ): Promise<CreateBucketResponse> => {
    return api.post(`/resources/${resourceId}/buckets`, request);
  },

  updateObjectStorageBucketQuota: async (
    resourceId: string,
    bucketName: string,
    quotaBucketGb: number,
  ): Promise<UpdateBucketQuotaResponse> => {
    return api.patch(`/resources/${resourceId}/buckets/${bucketName}`, {
      quotaBucketGb,
    });
  },

  resetObjectStoragePassword: async (
    resourceId: string,
  ): Promise<ResetStoragePasswordResponse> => {
    // Not replayed after a 5xx: each attempt generates a new password, so a retry would leave the
    // storage holding one the customer was never shown.
    return api.post(
      `/resources/${resourceId}/reset-password`,
      {},
      { _skipServerRetry: true },
    );
  },

  deleteObjectStorageBucket: async (
    resourceId: string,
    bucketName: string,
  ): Promise<void> => {
    return api.delete(`/resources/${resourceId}/buckets/${bucketName}`);
  },
};
